import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { PDF_BODY_CLASS, PDF_EYEBROW_CLASS } from "@/lib/proposalPdfTypography";
import { normalizePdfHighlights } from "@/lib/proposalPdfProductDisplay";
import {
  AudioLines,
  Layers,
  Monitor,
  Radio,
  Sparkles,
  Speaker,
  type LucideIcon,
} from "lucide-react";

const HIGHLIGHT_ICONS: LucideIcon[] = [Speaker, AudioLines, Layers, Monitor, Radio, Sparkles];

type Props = {
  highlights: string[];
};

function pickIcon(index: number): LucideIcon {
  return HIGHLIGHT_ICONS[index % HIGHLIGHT_ICONS.length] ?? Sparkles;
}

function columnCount(count: number): string {
  if (count <= 3) return "grid-cols-3";
  if (count === 4) return "grid-cols-4";
  return "grid-cols-5";
}

/** Compact visual highlight grid for PDF — each highlight is an independent cell. */
export default function DtoolsPdfHighlightsGrid({ highlights }: Props) {
  const items = normalizePdfHighlights(highlights);
  if (items.length === 0) return null;

  return (
    <section
      className="space-y-2"
      data-testid="dtools-pdf-highlights-grid"
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      <p className={PDF_EYEBROW_CLASS} style={{ color: "#C8A96A" }}>
        Key Highlights
      </p>
      <div className={`grid ${columnCount(items.length)} gap-2`}>
        {items.map((highlight, index) => {
          const Icon = pickIcon(index);
          return (
            <div
              key={`${highlight}-${index}`}
              className="pdf-keep-together flex min-w-0 flex-col items-center gap-1 border border-[#1A2744]/10 bg-white px-2 py-2 text-center"
              data-testid={`dtools-pdf-highlight-${index}`}
            >
              <Icon className="h-4 w-4 shrink-0 text-[#C8A96A]" aria-hidden />
              <span className={`block min-w-0 ${PDF_BODY_CLASS}`}>{highlight}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
